import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, ChevronDown, ChevronUp, Clock, CheckCircle, Target } from 'lucide-react';

interface SessionSummary {
  summary: string;
  keyAchievements: string[];
  recommendations: string[];
  duration: number;
  totalBuildCredits: number;
  tasksCompleted: number;
}

const SessionSummaryPanel = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sessionSummary, setSessionSummary] = useState<SessionSummary | null>(null);


  useEffect(() => {
    const handleSessionEnd = (event: Event) => {
      const customEvent = event as CustomEvent;
      setSessionSummary(customEvent.detail.summary);
      setIsExpanded(true);
    };

    window.addEventListener('beehive:session-end', handleSessionEnd);
    return () => {
      window.removeEventListener('beehive:session-end', handleSessionEnd);
    };
  }, []);

  if (!sessionSummary) {
    return null;
  }

  return (
    <motion.div 
      className="border-t border-muted bg-muted/20"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 flex items-center justify-between text-left hover:bg-muted/30 transition-colors"
        whileHover={{ backgroundColor: 'rgba(30, 41, 59, 0.3)' }}
      >
        <div className="flex items-center space-x-2">
          <FileText className="w-4 h-4 text-emerald-400" />
          <span className="text-sm font-medium text-slate-200">Session Summary</span>
          <span className="text-xs text-slate-400">({sessionSummary.tasksCompleted} tasks)</span>
        </div>
        
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </motion.button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 border-t border-muted/50 max-h-64 overflow-y-auto">
              <div className="mb-4">
                <h4 className="text-sm font-medium text-slate-200 mb-2">Summary</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  {sessionSummary.summary}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
                <div className="bg-muted/30 p-2 rounded">
                  <div className="text-slate-400 flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>Duration</span>
                  </div>
                  <div className="text-slate-200 font-mono">
                    {Math.round(sessionSummary.duration / 60)}m
                  </div>
                </div>
                <div className="bg-muted/30 p-2 rounded">
                  <div className="text-slate-400">Build Credits</div>
                  <div className="text-slate-200 font-mono">
                    {sessionSummary.totalBuildCredits.toLocaleString()} BC
                  </div>
                </div>
                <div className="bg-muted/30 p-2 rounded">
                  <div className="text-slate-400 flex items-center space-x-1">
                    <CheckCircle className="w-3 h-3" />
                    <span>Tasks</span>
                  </div>
                  <div className="text-slate-200 font-mono">
                    {sessionSummary.tasksCompleted}
                  </div>
                </div>
              </div>

              {sessionSummary.keyAchievements.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-slate-200 mb-2 flex items-center space-x-1">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span>Key Achievements</span>
                  </h4>
                  <ul className="space-y-1">
                    {sessionSummary.keyAchievements.map((achievement, index) => (
                      <li key={index} className="text-xs text-slate-300 flex items-start space-x-2">
                        <span className="text-emerald-400 mt-0.5">•</span>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {sessionSummary.recommendations.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-200 mb-2 flex items-center space-x-1">
                    <Target className="w-4 h-4 text-violet-400" />
                    <span>Recommendations</span>
                  </h4>
                  <ul className="space-y-1">
                    {sessionSummary.recommendations.map((recommendation, index) => (
                      <li key={index} className="text-xs text-slate-300 flex items-start space-x-2">
                        <span className="text-violet-400 mt-0.5">•</span>
                        <span>{recommendation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SessionSummaryPanel;
